import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { fetchUsersCheckInBlockers } from "src/models/user/user";
import { CheckInBlockerDialogProps } from "src/shared/CheckinBlocker/CheckInBlockerDialog";
import {
  CheckinBlockerOverrideContext,
  CheckinBlockerUser,
} from "src/shared/CheckinBlocker/_types";
import { Identifier } from "src/types/utilityTypes";

const PAYMENT_RESOLVED_CLOSE_DELAY_MS = 500;

function getUnresolvedUsers(users: CheckinBlockerUser[]) {
  return users
    .map((user) => ({
      ...user,
      blockers: user.blockers.filter((blocker) => !blocker.resolved),
    }))
    .filter((user) => user.blockers.length > 0);
}

function getOverrideResolvedUsers(users: CheckinBlockerUser[]) {
  return users
    .map((user) => ({
      ...user,
      blockers: user.blockers.filter(
        (blocker) => blocker.resolvedBy === 'override'
      ),
    }))
    .filter((user) => user.blockers.length > 0);
}

function getCloseResultUsers(users: CheckinBlockerUser[]) {
  const unresolvedUsers = getUnresolvedUsers(users);
  if (unresolvedUsers.length > 0) return unresolvedUsers;

  // Manual overrides already performed the requested action inline. Return a
  // non-empty result so callers do not repeat the original bulk check-in flow.
  return getOverrideResolvedUsers(users);
}

function applyOverrideToUser(
  user: CheckinBlockerUser,
  context: CheckinBlockerOverrideContext
): CheckinBlockerUser {
  if (context.scope === 'user') {
    return {
      ...user,
      blockers: user.blockers.map((blocker) =>
        !blocker.resolved
          ? { ...blocker, resolved: true, resolvedBy: 'override' as const }
          : blocker
      ),
    };
  }

  if (context.scope === 'blocker' && context.blockerIndex != null) {
    return {
      ...user,
      blockers: user.blockers.map((blocker, index) =>
        index === context.blockerIndex && !blocker.resolved
          ? { ...blocker, resolved: true, resolvedBy: 'override' as const }
          : blocker
      ),
    };
  }

  return user;
}

function applyOverrideToUsers(
  users: CheckinBlockerUser[],
  context: CheckinBlockerOverrideContext
) {
  const userId = context.user?.id;
  if (!userId) return users;

  return users.map((user) => {
    if (user.id == null || String(user.id) !== String(userId)) return user;

    return applyOverrideToUser(user, context);
  });
}

function scheduleCloseWhenAllResolved(
  nextUsers: CheckinBlockerUser[],
  closeTimerRef: { current: ReturnType<typeof setTimeout> | null },
  resolvePromiseRef: { current: ((value: CheckinBlockerUser[]) => void) | null },
  setOpen: (open: boolean) => void,
  setResolvePromise: (
    value: ((value: CheckinBlockerUser[]) => void) | null
  ) => void
) {
  if (getUnresolvedUsers(nextUsers).length === 0) {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
    }

    closeTimerRef.current = setTimeout(() => {
      setOpen(false);
      const promiseResolver = resolvePromiseRef.current;
      if (promiseResolver) {
        promiseResolver(getCloseResultUsers(nextUsers));
        setResolvePromise(null);
      }
      closeTimerRef.current = null;
    }, PAYMENT_RESOLVED_CLOSE_DELAY_MS);
  }
}

type UseCheckInBlockerOptions = Partial<
  Pick<CheckInBlockerDialogProps, 'title' | 'subtitle' | 'showUserDetails'> & {
    onOverride?: (context: CheckinBlockerOverrideContext) => void | Promise<void>;
  }
>;

export interface CheckBlockersInput {
  userIds?: Identifier[];
  barcodes?: string[];
  title?: string;
  subtitle?: string;
}

export type UseCheckInBlockerReturn = [
  CheckInBlockerDialogProps,
  (input: CheckBlockersInput) => Promise<CheckinBlockerUser[]>,
  (users: CheckinBlockerUser[]) => void,
  boolean,
];

export function useCheckInBlocker(
  initialOptions: UseCheckInBlockerOptions = {}
): UseCheckInBlockerReturn {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [users, setUsers] = useState<CheckinBlockerUser[]>([]);
  const [activeTitle, setActiveTitle] = useState<string | undefined>();
  const [activeSubtitle, setActiveSubtitle] = useState<string | undefined>();
  const [isLoading, setIsLoading] = useState(false);
  const [resolvePromise, setResolvePromise] = useState<
    ((value: CheckinBlockerUser[]) => void) | null
  >(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const checkBlockersRef = useRef<
    ((input: CheckBlockersInput) => Promise<CheckinBlockerUser[]>) | null
  >(null);
  const initialOptionsRef = useRef(initialOptions);
  const resolvePromiseRef = useRef(resolvePromise);
  const usersRef = useRef(users);

  initialOptionsRef.current = initialOptions;
  resolvePromiseRef.current = resolvePromise;
  usersRef.current = users;

  useEffect(() => {
    return () => {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
      }
    };
  }, []);

  const openWithUsers = (newUsers: CheckinBlockerUser[]) => {
    setUsers(newUsers);
    setOpen(true);
  };

  if (!checkBlockersRef.current) {
    checkBlockersRef.current = async ({
      userIds = [],
      barcodes = [],
      title,
      subtitle,
    }: CheckBlockersInput): Promise<CheckinBlockerUser[]> => {
      if (userIds.length === 0 && barcodes.length === 0) return [];

      setActiveTitle(title);
      setActiveSubtitle(subtitle);

      setIsLoading(true);
      const result = await queryClient.fetchQuery<CheckinBlockerUser[]>({
        queryKey: ['checkinBlockers', userIds, barcodes],
        queryFn: () => fetchUsersCheckInBlockers({ userIds, barcodes }),
      });
      setIsLoading(false);

      if (result.length === 0) return [];

      setUsers(result);
      setOpen(true);

      return new Promise((resolve) => {
        setResolvePromise(() => resolve);
      });
    };
  }

  const handleClose = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }

    setOpen(false);
    const promiseResolver = resolvePromiseRef.current;
    if (promiseResolver) {
      promiseResolver(getCloseResultUsers(usersRef.current));
      setResolvePromise(null);
    }
  };

  const handleBillingResolved = (userId: string) => {
    setUsers((currentUsers) => {
      const nextUsers = currentUsers.map((user) => {
        if (user.id == null || String(user.id) !== String(userId)) return user;

        return {
          ...user,
          blockers: user.blockers.map((blocker) =>
            blocker.type === 'billing_blocked'
              ? { ...blocker, resolved: true, resolvedBy: 'payment' as const }
              : blocker
          ),
        };
      });

      scheduleCloseWhenAllResolved(
        nextUsers,
        closeTimerRef,
        resolvePromiseRef,
        setOpen,
        setResolvePromise
      );

      return nextUsers;
    });
  };

  const handleOverride = async (context: CheckinBlockerOverrideContext) => {
    if (context.scope === 'all') {
      await initialOptionsRef.current.onOverride?.(context);
      return;
    }

    const userId = context.user?.id;
    if (!userId) return;

    let nextUsers = usersRef.current;
    flushSync(() => {
      setUsers((currentUsers) => {
        nextUsers = applyOverrideToUsers(currentUsers, context);
        return nextUsers;
      });
    });

    const updatedUser = nextUsers.find(
      (user) => user.id != null && String(user.id) === String(userId)
    );
    const userHasUnresolvedBlockers =
      updatedUser?.blockers.some((blocker) => !blocker.resolved) ?? true;
    const shouldInvokeConsumer =
      context.scope === 'user' ||
      (context.scope === 'blocker' && !userHasUnresolvedBlockers);

    if (shouldInvokeConsumer) {
      await initialOptionsRef.current.onOverride?.({
        ...context,
        scope: 'user',
        user: updatedUser,
      });
    }

    scheduleCloseWhenAllResolved(
      nextUsers,
      closeTimerRef,
      resolvePromiseRef,
      setOpen,
      setResolvePromise
    );
  };

  const dialogProps: CheckInBlockerDialogProps = {
    open,
    onClose: handleClose,
    title: activeTitle ?? initialOptions.title,
    subtitle: activeSubtitle ?? initialOptions.subtitle,
    showUserDetails: initialOptions.showUserDetails,
    onOverride: initialOptions.onOverride ? handleOverride : undefined,
    onBillingResolved: handleBillingResolved,
    users,
  };

  return [dialogProps, checkBlockersRef.current, openWithUsers, isLoading];
}
